import React, { useMemo } from "react"

import { AgentSelector, resolveAgent } from "@/agents"
import { useAgentsById } from "@/agents/catalog"
import { MetricsSelector } from "@/metrics/configure"
import { PersonaSelector } from "@/persona"
import { TemplateParamsSection } from "@/templates"
import { RunConfigurationCard, TestSetSelector } from "@/test-set"

type AgentSelectorProps = React.ComponentProps<typeof AgentSelector>
type PersonaSelectorProps = React.ComponentProps<typeof PersonaSelector>
type TestSetSelectorProps = React.ComponentProps<typeof TestSetSelector>
type MetricsSelectorProps = React.ComponentProps<typeof MetricsSelector>
type RunConfigurationProps = React.ComponentProps<typeof RunConfigurationCard>
type TemplateParamsProps = React.ComponentProps<typeof TemplateParamsSection>

const MetricsSelectorWithResolvedAgentId: React.FC<
  Omit<MetricsSelectorProps, "agentId"> & { rawFirstAgentId: string }
> = ({ rawFirstAgentId, ...metricsSelectorProps }) => {
  const { agentsById } = useAgentsById({
    // Without this, an agent created and selected in the same visit resolves
    // to "" here (stale lookup misses it), silently dropping agent-specific
    // metrics/eligibility instead of resolving the real agent id.
    refetchOnMount: true,
  })
  const resolvedAgentId = useMemo(
    () =>
      rawFirstAgentId
        ? (resolveAgent(rawFirstAgentId, agentsById)?.id ?? "")
        : "",
    [rawFirstAgentId, agentsById],
  )

  return <MetricsSelector {...metricsSelectorProps} agentId={resolvedAgentId} />
}

interface SimulationConfigFieldsProps {
  agent: Omit<AgentSelectorProps, "footer">
  persona: Omit<PersonaSelectorProps, "footer">
  testSet: Omit<TestSetSelectorProps, "footer">
  /**
   * Per-iteration test case params. Omitted by a caller with nowhere to store
   * them: a run template carries no field for these, so only a launch, which
   * can read them off a prior run, has something to put here.
   */
  templateParams?: TemplateParamsProps
  metrics: Omit<MetricsSelectorProps, "agentId"> & { rawFirstAgentId: string }
  /** Run-wide execution settings: iterations and concurrency. */
  configuration: RunConfigurationProps
  /** Pinned inside each selector's dropdown, for a caller that can create one. */
  agentFooter?: React.ReactNode
  personaFooter?: React.ReactNode
  testSetFooter?: React.ReactNode
  /** Rendered above the metrics list, where a caller has something to say first. */
  banner?: React.ReactNode
}

/**
 * The simulation configuration form: agent, persona, test set, per-iteration
 * params, metrics, and the run's execution settings, in that order. Rendered by the launcher and by the
 * template detail page, which configure the same thing and so ask the same
 * questions in the same order.
 *
 * Props are grouped per selector so both callers pass one shape and neither can
 * drift from the other's field set.
 */
export function SimulationConfigFields({
  agent,
  persona,
  testSet,
  templateParams,
  metrics,
  configuration,
  agentFooter,
  personaFooter,
  testSetFooter,
  banner,
}: SimulationConfigFieldsProps) {
  return (
    <div className="space-y-3">
      <AgentSelector {...agent} footer={agentFooter} />
      <PersonaSelector {...persona} footer={personaFooter} />
      <TestSetSelector {...testSet} footer={testSetFooter} />
      {templateParams && <TemplateParamsSection {...templateParams} />}
      {banner}
      <MetricsSelectorWithResolvedAgentId {...metrics} />
      <RunConfigurationCard {...configuration} />
    </div>
  )
}
